import { Link } from "react-router-dom";
import { useIncidents, useCurrentUser } from "@crm/api";
import { Ticket, Plus, Clock, CheckCircle, AlertCircle } from "lucide-react";
import { cn } from "@crm/utils";

const statusConfig: Record<string, { label: string; color: string }> = {
  new: { label: "Submitted", color: "bg-blue-100 text-blue-800" },
  assigned: { label: "Assigned", color: "bg-yellow-100 text-yellow-800" },
  in_progress: { label: "In Progress", color: "bg-purple-100 text-purple-800" },
  on_hold: { label: "Waiting", color: "bg-gray-100 text-gray-800" },
  resolved: { label: "Resolved", color: "bg-green-100 text-green-800" },
  closed: { label: "Closed", color: "bg-green-100 text-green-800" },
};

export function TicketsPage() {
  const { data: user } = useCurrentUser();
  // For client users, show all their tickets (reported by them)
  const { data, isLoading, error } = useIncidents();

  // Separate open and closed tickets
  const openTickets = data?.results?.filter(
    (i) => !["resolved", "closed"].includes(i.status)
  ) || [];
  const closedTickets = data?.results?.filter(
    (i) => ["resolved", "closed"].includes(i.status)
  ) || [];

  return (
    <div className="mx-auto max-w-4xl p-8">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            My Support Tickets
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Hi {user?.first_name}, here are your support requests.
          </p>
        </div>
        <Link
          to="/tickets/new"
          className="inline-flex h-10 items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          New Ticket
        </Link>
      </div>

      {/* Stats */}
      <div className="mb-8 grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-border bg-card p-6">
          <div className="flex items-center gap-4">
            <div className="rounded-md bg-yellow-100 p-3">
              <Clock className="h-5 w-5 text-yellow-700" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Open</p>
              <p className="text-2xl font-semibold text-foreground">
                {openTickets.length}
              </p>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-border bg-card p-6">
          <div className="flex items-center gap-4">
            <div className="rounded-md bg-green-100 p-3">
              <CheckCircle className="h-5 w-5 text-green-700" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Resolved</p>
              <p className="text-2xl font-semibold text-foreground">
                {closedTickets.length}
              </p>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-border bg-card p-6">
          <div className="flex items-center gap-4">
            <div className="rounded-md bg-blue-100 p-3">
              <Ticket className="h-5 w-5 text-blue-700" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="text-2xl font-semibold text-foreground">
                {(data?.results?.length) || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-8 text-center">
          <p className="text-destructive">Failed to load tickets</p>
        </div>
      ) : !data?.results?.length ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <Ticket className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-4 font-semibold text-foreground">No tickets yet</h3>
          <p className="mt-2 text-muted-foreground">
            Need help with something? Create a support ticket and we'll get back to you.
          </p>
          <Link
            to="/tickets/new"
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Create Ticket
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Open Tickets */}
          {openTickets.length > 0 && (
            <div>
              <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-foreground">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
                Open Tickets
              </h2>
              <div className="space-y-3">
                {openTickets.map((ticket) => {
                  const status = statusConfig[ticket.status] || statusConfig.new;
                  return (
                    <Link
                      key={ticket.id}
                      to={`/tickets/${ticket.id}`}
                      className="block rounded-lg border border-border bg-card p-4 transition-colors hover:bg-muted/50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium text-foreground">
                            {ticket.title}
                          </h3>
                          <p className="mt-1 text-sm text-muted-foreground">
                            Submitted {new Date(ticket.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                            status.color
                          )}
                        >
                          {status.label}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Closed Tickets */}
          {closedTickets.length > 0 && (
            <div>
              <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-foreground">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Resolved Tickets
              </h2>
              <div className="space-y-3">
                {closedTickets.map((ticket) => {
                  const status = statusConfig[ticket.status] || statusConfig.new;
                  return (
                    <Link
                      key={ticket.id}
                      to={`/tickets/${ticket.id}`}
                      className="block rounded-lg border border-border bg-card p-4 transition-colors hover:bg-muted/50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium text-foreground">
                            {ticket.title}
                          </h3>
                          <p className="mt-1 text-sm text-muted-foreground">
                            Resolved {ticket.resolved_at 
                              ? new Date(ticket.resolved_at).toLocaleDateString() 
                              : "recently"}
                          </p>
                        </div>
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                            status.color
                          )}
                        >
                          {status.label}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
